<template>
    <div>
        <!-- Page Header -->
        <section class="bg-dark pt-5 pb-4">
            <div class="container py-5">
                <h1 class="text-light pt-1 pt-md-3 mb-4">
                    Connecting Sage Account
                </h1>
                <p class="text-light opacity-75 mb-0">
                    Processing your authorization...
                </p>
            </div>
        </section>
        <!-- / Page Header -->

        <!-- Callback Content -->
        <section class="position-relative bg-white rounded-xxl-4 zindex-5" style="margin-top: -30px;">
            <div class="container pt-4 pb-5 mb-md-4">
                <!-- Breadcrumb-->
                <breadcrumb :crumbs="crumbs" class="pb-4 my-2"></breadcrumb>

                <!-- Processing Status -->
                <div class="text-center py-5">
                    <div v-if="isProcessing" class="mb-4">
                        <spinner class="mb-3"></spinner>
                        <h4 class="mb-2">{{ processingMessage }}</h4>
                        <p class="text-muted">{{ processingDetails }}</p>
                    </div>

                    <!-- Success State -->
                    <div v-else-if="isSuccess" class="mb-4">
                        <div class="text-success mb-3">
                            <i class="fi-check-circle fs-1"></i>
                        </div>
                        <h4 class="mb-2 text-success">Connection Successful!</h4>
                        <p class="text-muted mb-4">Your Sage Accounting account has been successfully connected.</p>
                        <div class="d-flex justify-content-center gap-2">
                            <router-link to="/integrations/sage" class="btn btn-primary">
                                <i class="fi-settings me-2"></i>
                                View Integration
                            </router-link>
                            <router-link to="/integrations" class="btn btn-outline-secondary">
                                <i class="fi-arrow-left me-2"></i>
                                Back to Integrations
                            </router-link>
                        </div>
                    </div>

                    <!-- Error State -->
                    <div v-else-if="isError" class="mb-4">
                        <div class="text-danger mb-3">
                            <i class="fi-x-circle fs-1"></i>
                        </div>
                        <h4 class="mb-2 text-danger">Connection Failed</h4>
                        <p class="text-muted mb-4">{{ errorMessage }}</p>
                        <div class="d-flex justify-content-center gap-2">
                            <button @click="retryConnection" class="btn btn-primary">
                                <i class="fi-refresh me-2"></i>
                                Try Again
                            </button>
                            <router-link to="/integrations/sage" class="btn btn-outline-secondary">
                                <i class="fi-arrow-left me-2"></i>
                                Back to Sage Integration
                            </router-link>
                        </div>
                    </div>
                </div>
                <!-- / Processing Status -->

            </div>
        </section>
        <!-- / Callback Content -->
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from '../../components/Spinner.vue';
import Breadcrumb from '../../components/Breadcrumb.vue';
import axios from 'axios';

export default {
    name: 'SageCallback',
    
    components: { 
        Spinner, 
        Breadcrumb 
    },

    data() {
        return {
            isProcessing: true,
            isSuccess: false,
            isError: false,
            processingMessage: 'Processing authorization...',
            processingDetails: 'Please wait while we complete your Sage connection.',
            errorMessage: 'An error occurred while connecting your Sage account.',
            code: null,
            state: null
        }
    },

    computed: {
        ...mapGetters('auth', {
            user: 'user',
            userRole: 'userRole',
            isAdvisor: 'isAdvisor'
        }),

        crumbs() {
            return [
                {
                    href: "/",
                    label: "Dashboard",
                },
                {
                    href: "/integrations",
                    label: "Integrations",
                },
                {
                    href: "/integrations/sage",
                    label: "Sage Accounting",
                },
                {
                    href: "#",
                    label: "Connecting...",
                    active: true,
                },
            ];
        }
    },

    created() {
        // Check if user is an advisor, if not redirect
        if (!this.isAdvisor) {
            this.$router.push('/');
            return;
        }

        this.processCallback();
    },

    methods: {
        async processCallback() {
            try {
                // Get URL parameters
                const urlParams = new URLSearchParams(window.location.search);
                this.code = urlParams.get('code');
                this.state = urlParams.get('state');

                // Check if we have the required parameters
                if (!this.code || !this.state) {
                    this.handleError('Missing authorization parameters. Please try connecting again.');
                    return;
                }

                // Update processing message
                this.processingMessage = 'Exchanging authorization code...';
                this.processingDetails = 'Securely exchanging your authorization code for access tokens.';

                // Process the callback locally first
                const callbackData = await this.processCallbackLocally();

                // Update processing message
                this.processingMessage = 'Sending to server...';
                this.processingDetails = 'Sending the processed data to our servers to complete the integration.';

                // Send the processed data to the server
                await this.sendToServer(callbackData);

                // Success!
                this.isProcessing = false;
                this.isSuccess = true;

            } catch (error) {
                console.error('Callback processing error:', error);
                this.handleError(error.message || 'Failed to complete the Sage connection.');
            }
        },

        async processCallbackLocally() {
            // This is where you would process the authorization code locally
            // For now, we'll just prepare the data to send to the server
            // In a real implementation, you might want to validate the state parameter here
            
                    return {
            code: this.code,
            state: this.state,
            processed_at: new Date().toISOString(),
            user_role_id: this.userRole ? this.userRole.id : null
        };
        },

        async sendToServer(callbackData) {
            try {
                const response = await axios.post(`${window.API_BASE}/sage/callback`, callbackData, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.data.integration_id) {
                    throw new Error('Server did not return integration ID');
                }

                return response.data;
            } catch (error) {
                console.error('Server callback error:', error);
                
                if (error.response && error.response.data && error.response.data.error) {
                    throw new Error(error.response.data.error);
                } else if (error.response && error.response.data && error.response.data.message) {
                    throw new Error(error.response.data.message);
                } else {
                    throw new Error('Failed to complete integration on server');
                }
            }
        },

        handleError(message) {
            this.isProcessing = false;
            this.isError = true;
            this.errorMessage = message;
        },

        retryConnection() {
            // Redirect back to the Sage integration page to try again
            this.$router.push('/integrations/sage');
        }
    }
}
</script>

<style scoped>
.text-success {
    color: #198754 !important;
}

.text-danger {
    color: #dc3545 !important;
}
</style> 